import { useMemo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useInfiniteQuery } from 'react-query'
import { yupResolver } from '@hookform/resolvers/yup'

import schema from '../../../../../../validators/assetAnalystSchema'
import { Button } from '../../../../../../components/Button'
import { Input } from '../../../../../../components/FormElements/Input'
import { api } from '../../../../../../services/api'
import { InfiniteSelect } from '../../../../../../components/InfiniteSelect'
import { FormGroup } from '../../../../../../components/FormElements/FormGroup'
import { type AssetAnalystDomain } from '../../../../../../types/domain/asset-analyst-domain'
import { type AssetAnalystPersistence } from '../../../../../../types/persistence/asset-analyst-persistence'
import { type AssetDomain } from '../../../../../../types/domain/asset-domain'

type AssetAnalystFormProps = {
  isSubmitting: boolean
  assetAnalyst?: AssetAnalystDomain
  onSubmit: (data: AssetAnalystPersistence) => void
}

type FormValues = {
  fullName: string
  assetId?: string
}

export const AssetAnalystForm = ({
  isSubmitting,
  assetAnalyst,
  onSubmit,
}: AssetAnalystFormProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const { control, formState, handleSubmit } = useForm<FormValues>({
    defaultValues: assetAnalyst && {
      fullName: assetAnalyst.fullName,
      assetId: assetAnalyst?.asset?.id
        ? String(assetAnalyst.asset?.id)
        : undefined,
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const { data, hasNextPage, fetchNextPage } = useInfiniteQuery(
    ['asset-select', searchTerm],
    ({ pageParam = 1 }) =>
      api
        .get(
          `/assets?page=${pageParam}&search=${searchTerm}&sort=fullName&order=ASC`
        )
        .then((response) => response.data),
    {
      getNextPageParam ({ meta }) {
        return meta?.hasNextPage ? meta?.page + 1 : undefined
      },
    }
  )

  const assetOptions = useMemo(() => {
    const allAssets: AssetDomain[] = data?.pages.reduce((acc, { data }) => {
      return [...acc, ...data]
    }, []) || []

    if (allAssets.length === 0) {
      return [{ label: 'Sem asset cadastrado', value: '' }]
    }

    const options = allAssets.map((asset) => ({
      label: asset.fullName,
      value: String(asset.id),
    }))

    const selectedAsset = assetAnalyst?.asset
    const assetAlreadySelected = allAssets.some((asset) => (
      String(asset.id) === String(selectedAsset?.id)
    ))

    if (selectedAsset && !assetAlreadySelected) {
      options.push({
        label: selectedAsset?.fullName,
        value: String(selectedAsset.id)
      })
    }

    return [
      { label: 'Sem asset', value: '' },
      ...options
    ]
  }, [data?.pages, assetAnalyst?.asset])

  const handleSave = (data: FormValues) => {
    onSubmit({
      ...data,
      assetId: data.assetId === '' ? null : Number(data.assetId),
    })
  }

  return (
    <form
      className="p-6 max-h-[80vh] overflow-y-auto scrollbar"
      onSubmit={handleSubmit(handleSave)}
    >
      <div className="mb-14">
        <div className="mb-8">
          <div className="mb-4">
            <Controller
              name="fullName"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <Input
                  label="Nome completo"
                  errorMessage={error?.message}
                  autoFocus
                  placeholder="Nome completo"
                  isValid={!invalid && !!error}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </div>

          <Controller
            name="assetId"
            control={control}
            render={({
              field: { value = '', onChange },
              fieldState: { error },
            }) => (
              <FormGroup label="Asset" error={error?.message}>
                <InfiniteSelect
                  hasNextPage={hasNextPage}
                  searchInputPlaceholder="Buscar pelo asset"
                  defaultValue={String(value)}
                  options={assetOptions}
                  onLoadMoreOptions={fetchNextPage}
                  onValueChange={(value) => onChange(value)}
                  onSearch={(searchTerm: string) => setSearchTerm(searchTerm)}
                />
              </FormGroup>
            )}
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={!formState.isValid}
        isLoading={isSubmitting}
        fullWidth
      >
        {assetAnalyst ? 'Salvar' : 'Cadastrar'}
      </Button>
    </form>
  )
}
